import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Save, X, Archive, ArchiveRestore, RotateCcw, Download, Upload, Eye,
  Loader2, ChevronDown, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const emptyChar = (order) => ({
  key: '', name_en: '', name_hi: '', type: 'numeric', unit: '', section: 'hourly',
  sample_size: 0, spec_source: 'drawing', drawing_section: '', drawing_field: '',
  drawing_field_alternates: [], job_field: '', spec_low: null, spec_high: null,
  spec_text: '', display_order: order, computed: '', compute_sources: [],
  multi_location: false, spec_bound: '', archived: false,
});

const slugify = (s) => (s || '').toLowerCase().trim()
  .replace(/[^a-z0-9\s_]/g, '').replace(/\s+/g, '_').slice(0, 40);

/**
 * TemplateEditor
 * Props: open, onClose, initial (existing template or null for new), onSaved, onArchived
 * Admin/verify-only. See parent for permission gate.
 */
export default function TemplateEditor({ open, onClose, initial, onSaved, onArchived }) {
  const isNew = !initial;
  const [saving, setSaving] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(-1);
  const [showPreview, setShowPreview] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [stageIndex, setStageIndex] = useState('');
  const [stageName, setStageName] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [titleHi, setTitleHi] = useState('');
  const [docNo, setDocNo] = useState('');
  const [sampleSize, setSampleSize] = useState(2);
  const [frequency, setFrequency] = useState('Hourly');
  const [chars, setChars] = useState([]);

  // Stage picker (only used when creating new)
  const [stagePickMode, setStagePickMode] = useState('existing'); // 'existing' | 'custom'
  const [stageCatalog, setStageCatalog] = useState(null);
  const [drawingSchema, setDrawingSchema] = useState({ sections: [] });

  const loadFromInitial = useCallback((tpl) => {
    setStageIndex(tpl?.stage_index ?? '');
    setStageName(tpl?.stage_name || '');
    setTitleEn(tpl?.title_en || '');
    setTitleHi(tpl?.title_hi || '');
    setDocNo(tpl?.doc_no || '');
    setSampleSize(tpl?.sample_size ?? 2);
    setFrequency(tpl?.frequency || 'Hourly');
    const sorted = (tpl?.characteristics || []).slice().sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setChars(sorted.map((c, i) => ({ ...emptyChar(i + 1), ...c, display_order: i + 1 })));
  }, []);

  useEffect(() => {
    if (open) {
      if (initial) loadFromInitial(initial);
      else {
        setStageIndex(''); setStageName(''); setTitleEn(''); setTitleHi('');
        setDocNo(''); setSampleSize(2); setFrequency('Hourly');
        setChars([emptyChar(1)]);
        setStagePickMode('existing');
      }
      setExpandedIdx(-1);
      setConfirmReset(false); setConfirmArchive(false); setShowPreview(false);
      // Preload catalog + drawing schema (only for editor sessions where they're used)
      axios.get(`${API}/api/inline-quality/stage-catalog`, auth())
        .then((r) => setStageCatalog(r.data)).catch(() => setStageCatalog(null));
      axios.get(`${API}/api/inline-quality/drawing-schema`, auth())
        .then((r) => setDrawingSchema(r.data || { sections: [] })).catch(() => setDrawingSchema({ sections: [] }));
    }
  }, [open, initial, loadFromInitial]);

  const visibleChars = useMemo(() => chars.map((c, i) => ({ ...c, _origIdx: i }))
    .filter((c) => showArchived || !c.archived), [chars, showArchived]);
  const archivedCount = useMemo(() => chars.filter((c) => c.archived).length, [chars]);

  const setCharField = (idx, field, value) => {
    setChars((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'name_en' && !next[idx].key) {
        next[idx].key = slugify(value);
      }
      return next;
    });
  };

  const addChar = () => {
    setChars((prev) => [...prev, emptyChar(prev.length + 1)]);
    setExpandedIdx(chars.length);
  };
  const moveChar = (idx, dir) => {
    setChars((prev) => {
      const next = prev.slice();
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next.map((c, i) => ({ ...c, display_order: i + 1 }));
    });
  };
  const toggleArchiveChar = (idx) => setCharField(idx, 'archived', !chars[idx].archived);

  // Stage picker: candidate list = job stages that don't have a template yet
  const availableExistingStages = useMemo(() => {
    if (!stageCatalog) return [];
    const templated = new Set(stageCatalog.templates.map((t) => t.stage_index));
    return stageCatalog.job_stages.filter((s) => !templated.has(s.stage_index));
  }, [stageCatalog]);

  const pickExistingStage = (idx) => {
    const s = availableExistingStages.find((x) => x.stage_index === Number(idx));
    if (s) { setStageIndex(s.stage_index); setStageName(s.stage_name); }
  };

  const ensureUniqueKey = (chars_) => {
    // Auto-generate a unique key if empty or duplicate
    const used = new Set();
    return chars_.map((c) => {
      let k = c.key || slugify(c.name_en) || 'field';
      let i = 1;
      const base = k;
      while (used.has(k)) { k = `${base}_${++i}`; }
      used.add(k);
      return { ...c, key: k };
    });
  };

  const validate = () => {
    if (isNew) {
      if (stagePickMode === 'custom' && !stageName.trim()) {
        toast.error('Stage name is required'); return false;
      }
      if (stagePickMode === 'existing' && stageIndex === '') {
        toast.error('Pick an existing stage from the dropdown'); return false;
      }
    }
    if (stageIndex === '' || isNaN(Number(stageIndex))) {
      toast.error('Stage index missing'); return false;
    }
    if (!stageName.trim()) { toast.error('Stage name is required'); return false; }
    for (const c of chars) {
      if (!c.archived && !c.name_en?.trim()) {
        toast.error(`Row #${c.display_order}: name required`); return false;
      }
      if (c.spec_source === 'fixed' && c.type === 'numeric' && c.spec_low == null && c.spec_high == null) {
        toast.error(`Row #${c.display_order} ("${c.name_en}"): Fixed spec needs Low or High`); return false;
      }
      if (c.spec_source === 'drawing' && !c.drawing_field?.trim()) {
        toast.error(`Row #${c.display_order} ("${c.name_en}"): Pick a drawing field`); return false;
      }
    }
    return true;
  };

  const buildPayload = () => ({
    stage_index: Number(stageIndex),
    stage_name: stageName.trim(),
    title_en: titleEn.trim() || `Daily Validation Checksheet — ${stageName.trim()}`,
    title_hi: titleHi.trim(),
    doc_no: docNo.trim(),
    sample_size: Number(sampleSize) || 2,
    frequency: frequency.trim() || 'Hourly',
    characteristics: ensureUniqueKey(chars).map((c, i) => ({
      ...c,
      display_order: i + 1,
      spec_low: c.spec_low === '' || c.spec_low == null ? null : Number(c.spec_low),
      spec_high: c.spec_high === '' || c.spec_high == null ? null : Number(c.spec_high),
      drawing_field_alternates: Array.isArray(c.drawing_field_alternates) ? c.drawing_field_alternates :
        (c.drawing_field_alternates || '').split(';').map((s) => s.trim()).filter(Boolean),
      compute_sources: Array.isArray(c.compute_sources) ? c.compute_sources :
        (c.compute_sources || '').split(';').map((s) => s.trim()).filter(Boolean),
    })),
  });

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/inline-quality/templates`, buildPayload(), auth());
      toast.success(isNew ? `Template "${stageName}" created` : `Template saved · version ${r.data.version}`);
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const archiveStage = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/inline-quality/templates/${initial.stage_index}/archive`, {}, auth());
      toast.success('Stage archived');
      onArchived?.(r.data);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
    finally { setSaving(false); }
  };
  const unarchiveStage = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/inline-quality/templates/${initial.stage_index}/unarchive`, {}, auth());
      toast.success('Stage un-archived');
      onArchived?.(r.data);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Un-archive failed'); }
    finally { setSaving(false); }
  };
  const resetToFactory = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/inline-quality/templates/${initial.stage_index}/reset-to-factory`, {}, auth());
      toast.success('Reset to factory template');
      loadFromInitial(r.data);
      setConfirmReset(false);
      onSaved?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
    finally { setSaving(false); }
  };

  const downloadExcel = async () => {
    if (isNew) { toast.error('Save the new template first, then export'); return; }
    try {
      const r = await axios.get(`${API}/api/inline-quality/templates/${initial.stage_index}/export`, {
        ...auth(), responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${initial.stage_name || 'stage'}.xlsx`.replace(/\s+/g, '_');
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Excel export failed'); }
  };
  const uploadExcel = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/inline-quality/templates/import`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Excel imported successfully');
      loadFromInitial(r.data);
      onSaved?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Excel import failed'); }
    finally { setSaving(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="iq-template-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {isNew ? 'Create Checksheet Template' : `Edit — ${initial?.stage_name || 'Template'}`}
            {!isNew && initial?.version != null && (
              <Badge variant="outline" data-testid="iq-tpl-version">v{initial.version}</Badge>
            )}
            {!isNew && initial?.edited_by_admin && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Custom (edited)</Badge>
            )}
            {!isNew && initial?.archived && (
              <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Archived</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Live sheets keep their existing spec snapshot — your edits only apply to new sheets started after save.
          </DialogDescription>
        </DialogHeader>

        {/* Stage picker (only shown when creating new) */}
        {isNew && (
          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">Which stage is this for?</div>
            <div className="flex gap-4 text-sm mb-2">
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="iq-tpl-stage-mode-existing">
                <input type="radio" checked={stagePickMode === 'existing'} onChange={() => setStagePickMode('existing')} />
                An existing production stage
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="iq-tpl-stage-mode-custom">
                <input type="radio" checked={stagePickMode === 'custom'} onChange={() => {
                  setStagePickMode('custom');
                  if (stageCatalog && !stageIndex) setStageIndex(stageCatalog.next_custom_index);
                }} />
                A brand-new stage (e.g. Salt Spray, MPI)
              </label>
            </div>
            {stagePickMode === 'existing' ? (
              <div>
                <select value={stageIndex} onChange={(e) => pickExistingStage(e.target.value)}
                  className="w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white"
                  data-testid="iq-tpl-existing-stage-select">
                  <option value="">— Pick a job-flow stage without a checksheet yet —</option>
                  {availableExistingStages.map((s) => (
                    <option key={s.stage_index} value={s.stage_index}>
                      Stage {s.stage_index} · {s.stage_name}  (used in {s.used_in} job records)
                    </option>
                  ))}
                </select>
                {availableExistingStages.length === 0 && (
                  <div className="text-[11px] text-slate-500 mt-1">All production stages already have a checksheet. Pick &quot;brand-new&quot; above to add something like Salt Spray or MPI Batch Test.</div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2">
                  <Label className="text-xs">New stage name</Label>
                  <Input value={stageName} onChange={(e) => setStageName(e.target.value)}
                    placeholder="e.g. Salt Spray Test · Magnetic Particle Inspection"
                    data-testid="iq-tpl-stage-name" />
                </div>
                <div>
                  <Label className="text-xs">Auto-assigned stage number</Label>
                  <Input value={stageIndex} readOnly className="bg-slate-100 text-slate-500 font-mono"
                    data-testid="iq-tpl-stage-index" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header fields — for existing edit and after stage pick */}
        {(!isNew || stageName) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Title (English) <span className="text-slate-400 text-[11px]">— optional, auto-generated if blank</span></Label>
              <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)}
                placeholder={`Daily Validation Checksheet — ${stageName || 'Stage'}`}
                data-testid="iq-tpl-title-en" />
            </div>
            <div>
              <Label>Title (Hindi)</Label>
              <Input value={titleHi} onChange={(e) => setTitleHi(e.target.value)}
                placeholder="डेली वेलिडेशन" data-testid="iq-tpl-title-hi" />
            </div>
            <div>
              <Label>Doc No</Label>
              <Input value={docNo} onChange={(e) => setDocNo(e.target.value)}
                placeholder="ASPL/QC/…" data-testid="iq-tpl-doc-no" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sample Size</Label>
                <Input type="number" value={sampleSize} onChange={(e) => setSampleSize(e.target.value)}
                  min={1} data-testid="iq-tpl-sample-size" />
              </div>
              <div>
                <Label>Frequency</Label>
                <Input value={frequency} onChange={(e) => setFrequency(e.target.value)}
                  placeholder="Hourly · Daily · Per Batch" data-testid="iq-tpl-frequency" />
              </div>
            </div>
          </div>
        )}

        {/* Characteristics list */}
        {(!isNew || stageName) && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-700">
                Characteristics ({chars.filter((c) => !c.archived).length}{archivedCount > 0 && `, ${archivedCount} archived`})
              </div>
              {archivedCount > 0 && (
                <button type="button" onClick={() => setShowArchived(!showArchived)}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline">
                  {showArchived ? 'Hide' : 'Show'} archived
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={downloadExcel} disabled={isNew || saving}
                data-testid="iq-tpl-export-xlsx">
                <Download className="w-4 h-4 mr-1" />Excel
              </Button>
              <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden"
                onChange={(e) => uploadExcel(e.target.files?.[0])} data-testid="iq-tpl-import-input" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={saving}
                data-testid="iq-tpl-import-xlsx">
                <Upload className="w-4 h-4 mr-1" />Import
              </Button>
              <Button size="sm" onClick={addChar} data-testid="iq-tpl-add-row">
                <Plus className="w-4 h-4 mr-1" />Add row
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {visibleChars.map((c) => {
              const i = c._origIdx;
              const expanded = expandedIdx === i;
              return (
                <CharRow
                  key={`row-${i}`}
                  c={c}
                  i={i}
                  totalCount={chars.length}
                  expanded={expanded}
                  drawingSchema={drawingSchema}
                  onToggle={() => setExpandedIdx(expanded ? -1 : i)}
                  onSetField={(f, v) => setCharField(i, f, v)}
                  onMove={(dir) => moveChar(i, dir)}
                  onArchive={() => toggleArchiveChar(i)}
                />
              );
            })}
            {visibleChars.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                No characteristics yet. Click <span className="font-semibold">+ Add row</span> to start.
              </div>
            )}
          </div>
        </div>
        )}

        {/* Footer actions */}
        <div className="flex flex-wrap justify-between gap-2 mt-4 pt-3 border-t border-slate-200">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="iq-tpl-cancel">
              <X className="w-4 h-4 mr-1" />Close
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)} disabled={chars.length === 0}
              data-testid="iq-tpl-preview">
              <Eye className="w-4 h-4 mr-1" />{showPreview ? 'Hide' : 'Preview'}
            </Button>
            {!isNew && (
              <>
                {!initial.archived ? (
                  !confirmArchive ? (
                    <Button variant="outline" onClick={() => setConfirmArchive(true)} disabled={saving}
                      className="text-amber-700 border-amber-300"
                      data-testid="iq-tpl-archive-stage">
                      <Archive className="w-4 h-4 mr-1" />Archive stage
                    </Button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-amber-700 font-semibold">Archive whole stage?</span>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(false)}>Cancel</Button>
                      <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                        onClick={archiveStage} data-testid="iq-tpl-archive-confirm">Yes archive</Button>
                    </div>
                  )
                ) : (
                  <Button variant="outline" onClick={unarchiveStage} disabled={saving}
                    className="text-emerald-700 border-emerald-300"
                    data-testid="iq-tpl-unarchive-stage">
                    <ArchiveRestore className="w-4 h-4 mr-1" />Un-archive stage
                  </Button>
                )}
                {!confirmReset ? (
                  <Button variant="outline" onClick={() => setConfirmReset(true)} disabled={saving}
                    data-testid="iq-tpl-reset">
                    <RotateCcw className="w-4 h-4 mr-1" />Reset to factory
                  </Button>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-red-700 font-semibold">Discard all admin edits?</span>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmReset(false)}>Cancel</Button>
                    <Button size="sm" className="bg-red-600 text-white hover:bg-red-700"
                      onClick={resetToFactory} data-testid="iq-tpl-reset-confirm">Yes reset</Button>
                  </div>
                )}
              </>
            )}
          </div>
          <Button onClick={save} disabled={saving} className="bg-slate-800 text-white hover:bg-slate-900"
            data-testid="iq-tpl-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isNew ? 'Create template' : 'Save changes'}
          </Button>
        </div>

        {/* Preview panel */}
        {showPreview && (
          <div className="mt-4 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50/30 p-4"
            data-testid="iq-tpl-preview-panel">
            <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">Preview — how the operator will see this sheet</div>
            <div className="text-lg font-bold">{titleEn || (stageName ? `Daily Validation Checksheet — ${stageName}` : 'Untitled')}</div>
            {titleHi && <div className="text-sm text-slate-600">{titleHi}</div>}
            <div className="text-xs text-slate-500 mt-1">Doc {docNo || '—'} · Sample size {sampleSize} · {frequency}</div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-600">
                    <th className="px-2 py-1 text-left">#</th>
                    <th className="px-2 py-1 text-left">Characteristic</th>
                    <th className="px-2 py-1 text-left">Type / Unit</th>
                    <th className="px-2 py-1 text-left">Spec preview</th>
                  </tr>
                </thead>
                <tbody>
                  {chars.filter((c) => !c.archived).map((c, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="px-2 py-1">{i + 1}</td>
                      <td className="px-2 py-1 font-medium">
                        {c.name_en}
                        {c.multi_location && <Badge variant="outline" className="ml-1 text-[9px]">multi-loc</Badge>}
                      </td>
                      <td className="px-2 py-1 text-slate-500">{c.type}{c.unit ? ` · ${c.unit}` : ''}</td>
                      <td className="px-2 py-1 text-slate-600">
                        {c.spec_source === 'fixed' && c.spec_low != null && c.spec_high != null
                          ? `${c.spec_low} – ${c.spec_high} ${c.unit || ''}`
                          : c.spec_source === 'drawing' && c.drawing_field
                          ? `📐 ${c.drawing_section} / ${c.drawing_field}${c.spec_bound === 'max' ? ' (Max)' : c.spec_bound === 'min' ? ' (Min)' : ''}`
                          : c.spec_text || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {chars.filter((c) => !c.archived).length === 0 && (
                <div className="text-center text-slate-400 py-4">No active characteristics</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------- CharRow: simplified per-row editor ----------------

const specPreview = (c) => {
  const u = c.unit ? ` ${c.unit}` : '';
  if (c.spec_source === 'fixed' && (c.spec_low != null || c.spec_high != null)) {
    if (c.spec_low != null && c.spec_high != null) return `${c.spec_low} – ${c.spec_high}${u}`;
    if (c.spec_low != null) return `Min ${c.spec_low}${u}`;
    return `Max ${c.spec_high}${u}`;
  }
  if (c.spec_source === 'drawing') {
    const src = c.drawing_field || '(no field)';
    const bound = c.spec_bound === 'max' ? ' · Max' : c.spec_bound === 'min' ? ' · Min' : '';
    return `📐 ${src}${bound}`;
  }
  if (c.spec_source === 'job') return `📋 job.${c.job_field || '?'}`;
  if (c.computed === 'avg') return `Σ avg of ${(c.compute_sources || []).length} fields`;
  if (c.computed === 'diff_abs') return `Σ |a − b|`;
  return c.spec_text || '—';
};

function CharRow({ c, i, totalCount, expanded, drawingSchema, onToggle, onSetField, onMove, onArchive }) {
  const isDrawing = c.spec_source === 'drawing';
  const isFixed = c.spec_source === 'fixed' && c.type === 'numeric';

  // Section options from drawing library
  const sectionOptions = (drawingSchema?.sections || []).map((s) => s.section_name);
  const fieldOptionsForCurrentSection = useMemo(() => {
    if (!isDrawing || !c.drawing_section) return [];
    const s = (drawingSchema?.sections || []).find((x) => x.section_name === c.drawing_section);
    return s ? s.fields : [];
  }, [isDrawing, c.drawing_section, drawingSchema]);

  const onPickDrawingField = (fieldName) => {
    onSetField('drawing_field', fieldName);
    // Auto-suggest unit if drawing has one and this row has no unit yet
    if (!c.unit) {
      const f = fieldOptionsForCurrentSection.find((x) => x.field_name === fieldName);
      if (f && f.unit_hint) {
        // Clean unit hint like "µm (Max.)" -> "µm" and infer bound
        const cleaned = f.unit_hint.replace(/\s*\((max|min)\.?\s*\)\s*/i, '').trim();
        const boundMatch = f.unit_hint.match(/\((max|min)\.?\s*\)/i);
        onSetField('unit', cleaned);
        if (boundMatch && !c.spec_bound) onSetField('spec_bound', boundMatch[1].toLowerCase());
      }
    }
  };

  return (
    <div className={`rounded-lg border ${c.archived ? 'border-slate-300 bg-slate-50 opacity-70' : 'border-slate-200 bg-white'}`}
      data-testid={`iq-tpl-row-${i}`}>
      {/* Row header — compact one-liner with spec preview */}
      <div className="flex items-center gap-2 p-2">
        <div className="flex flex-col">
          <button type="button" onClick={() => onMove(-1)} disabled={i === 0}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 -mb-1" title="Move up"
            data-testid={`iq-tpl-row-${i}-up`}>
            <ChevronRight className="w-3 h-3 -rotate-90" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={i === totalCount - 1}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 -mt-1" title="Move down"
            data-testid={`iq-tpl-row-${i}-down`}>
            <ChevronRight className="w-3 h-3 rotate-90" />
          </button>
        </div>
        <div className="text-xs font-mono text-slate-400 w-8 text-center">#{i + 1}</div>
        <div className="flex-1 min-w-0">
          <button type="button" onClick={onToggle}
            className="w-full text-left"
            data-testid={`iq-tpl-row-${i}-toggle`}>
            <div className={`font-medium text-sm ${c.archived ? 'line-through text-slate-400' : 'text-slate-800'}`}>
              {c.name_en || <span className="text-slate-400 italic">Untitled</span>}
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span>{c.type}{c.unit ? ` · ${c.unit}` : ''}</span>
              <span className="text-slate-300">•</span>
              <span className="truncate">{specPreview(c)}</span>
              {c.multi_location && <Badge variant="outline" className="text-[9px] px-1 py-0">multi-entry</Badge>}
              {c.archived && <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[9px] px-1 py-0">archived</Badge>}
              {c.computed && <Badge variant="outline" className="text-[9px] px-1 py-0">auto-calc</Badge>}
            </div>
          </button>
        </div>
        <button type="button" onClick={onToggle}
          className="text-slate-400 hover:text-slate-800 p-1">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded body — Basics + Specification only */}
      {expanded && (
        <div className="border-t border-slate-200 p-3 space-y-3 bg-slate-50/40">
          {/* ── Basics ── */}
          <section>
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">Basics</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={c.name_en} onChange={(e) => onSetField('name_en', e.target.value)}
                  placeholder="e.g. Taper Length — Face 1"
                  data-testid={`iq-tpl-row-${i}-name`} />
              </div>
              <div>
                <Label className="text-xs">Name (Hindi, optional)</Label>
                <Input value={c.name_hi} onChange={(e) => onSetField('name_hi', e.target.value)}
                  placeholder="हिंदी में नाम" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select value={c.type} onChange={(e) => onSetField('type', e.target.value)}
                  className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                  data-testid={`iq-tpl-row-${i}-type`}>
                  <option value="numeric">Numeric (measured value)</option>
                  <option value="attribute">Attribute (Ok / Not Ok)</option>
                  <option value="text">Text (free note)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Unit</Label>
                  <Input value={c.unit} onChange={(e) => onSetField('unit', e.target.value)}
                    placeholder="mm · °C · %" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!c.multi_location}
                      onChange={(e) => onSetField('multi_location', e.target.checked)}
                      data-testid={`iq-tpl-row-${i}-multi-loc`} />
                    Multi-entry (L1; L2; …)
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* ── Spec ── */}
          <section>
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">Specification</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="md:col-span-2">
                <Label className="text-xs">Where does the spec come from?</Label>
                <select value={c.spec_source} onChange={(e) => onSetField('spec_source', e.target.value)}
                  className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                  data-testid={`iq-tpl-row-${i}-spec-src`}>
                  <option value="drawing">📐 From drawing (auto-pull tolerance)</option>
                  <option value="fixed">🔢 Fixed values (I&apos;ll type them)</option>
                  <option value="none">— No spec (visual / manual only)</option>
                </select>
              </div>
              {isDrawing && (
                <>
                  <div>
                    <Label className="text-xs">Drawing section</Label>
                    <select value={c.drawing_section}
                      onChange={(e) => { onSetField('drawing_section', e.target.value); onSetField('drawing_field', ''); }}
                      className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                      data-testid={`iq-tpl-row-${i}-drawing-section`}>
                      <option value="">— pick a section —</option>
                      {sectionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Drawing field</Label>
                    <select value={c.drawing_field}
                      onChange={(e) => onPickDrawingField(e.target.value)}
                      disabled={!c.drawing_section}
                      className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
                      data-testid={`iq-tpl-row-${i}-drawing-field`}>
                      <option value="">
                        {c.drawing_section ? '— pick a field —' : '(pick a section first)'}
                      </option>
                      {fieldOptionsForCurrentSection.map((f) => (
                        <option key={f.field_name} value={f.field_name}>
                          {f.field_name}{f.unit_hint ? ` — ${f.unit_hint}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">If the drawing has just a single value, treat it as</Label>
                    <select value={c.spec_bound || ''} onChange={(e) => onSetField('spec_bound', e.target.value)}
                      className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white">
                      <option value="">— use drawing as-is (range or tolerance) —</option>
                      <option value="max">Max (upper limit)</option>
                      <option value="min">Min (lower limit)</option>
                    </select>
                    <div className="text-[10px] text-slate-400 mt-0.5">Only used when the drawing stores a bare number without a Min/Max qualifier.</div>
                  </div>
                </>
              )}
              {isFixed && (
                <>
                  <div>
                    <Label className="text-xs">Low limit</Label>
                    <Input type="number" step="any" value={c.spec_low ?? ''}
                      onChange={(e) => onSetField('spec_low', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="e.g. 850" />
                  </div>
                  <div>
                    <Label className="text-xs">High limit</Label>
                    <Input type="number" step="any" value={c.spec_high ?? ''}
                      onChange={(e) => onSetField('spec_high', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="e.g. 920" />
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <Label className="text-xs">Fallback text {isDrawing && '(shown when drawing has no tolerance yet)'}</Label>
                <Input value={c.spec_text} onChange={(e) => onSetField('spec_text', e.target.value)}
                  placeholder="e.g. As per drawing · Ok / Not Ok · 40 - 70 °C" />
              </div>
              {c.computed && (
                <div className="md:col-span-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
                  ⚙ This field is auto-calculated from other rows ({c.computed === 'avg' ? 'average' : 'difference'} of {(c.compute_sources || []).join(', ') || '—'}). Contact your system admin to change the formula.
                </div>
              )}
            </div>
          </section>

          <div className="flex justify-end pt-1">
            <Button variant="outline" size="sm" onClick={onArchive}
              className={c.archived ? 'text-emerald-700 border-emerald-300' : 'text-amber-700 border-amber-300'}
              data-testid={`iq-tpl-row-${i}-archive`}>
              {c.archived ? <><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Un-archive row</> : <><Archive className="w-3.5 h-3.5 mr-1" />Archive row</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
